import { type ReactNode } from "react";
import { Icon, Tab, T, tx } from "@v0y4ge/jrni-ui";
import { useNav } from "./router";
import { useUser } from "./access";
import { IconRail, MobileBottomNav } from "./chrome-rail";
import { Topbar } from "./chrome-topbar";
import { ScheduleAckBanner } from "./ScheduleAckBanner";
import { useIsMobile } from "./useResponsive";
import { useT } from "../lib/i18n";

/**
 * Screen / PrimaryNav / PageHeader — verbatim port of the prototype's layout.
 * <Screen> wraps every view: Topbar + left IconRail + optional section tabs +
 * content. The impersonation banner is NOT here — it mounts at the app root
 * (App.tsx), because acting-as has to be visible on views that never render a
 * Screen at all (the launcher's no-apps shell, where a narrow-role target
 * lands).
 */

/* The page background behind every screen is `T.canvas` again (2026-08-07,
 * Pat, operator: "fix the grey background Nick added and make it like
 * before"). The 2026-08-05 `SHELL_CANVAS = "#E5E5E5"` literal is REVERTED —
 * see changelog/2026-08-05T1455-shell-canvas-e5e5e5.md for what it was and
 * changelog/2026-08-07T1500-shell-canvas-revert.md for why it went. */

export interface PrimaryNavTab { id: string; label: ReactNode; count?: number; onClick?: () => void; group?: string; }

/** Tabs may carry a `group`. A flat strip of eight tabs asserts eight equal
 *  surfaces; when the tabs answer different questions, a rule between the
 *  clusters is enough to stop the reader treating them as interchangeable.
 *  The group value is an internal id and is NEVER rendered (operator,
 *  2026-08-31 — the label words were noise): order plus the divider carries
 *  the grouping. Ungrouped tabs render exactly as before. */
export function PrimaryNav({ tabs, active }: { tabs: PrimaryNavTab[]; active?: string }) {
  const t = useT();
  const grouped = tabs.some((x) => x.group);
  return (
    <nav aria-label={t("chrome.layout.sectionTabs", "Section tabs")} role="tablist" style={{ display: "flex", gap: 4, padding: "0 24px", background: T.white, borderBottom: `1px solid ${T.border}`, flexShrink: 0, alignItems: "stretch", overflowX: "auto", overflowY: "hidden" /* sideways only: the active tab's underline overflowed 1px and drew a stray vertical scrollbar (walk 3 item 7) */ }}>
      {tabs.map((x, i) => {
        const newGroup = grouped && !!i && !!x.group && x.group !== tabs[i - 1]?.group;
        return (
          <span key={x.id} style={{ display: "flex", alignItems: "stretch", gap: 4 }}>
            {newGroup ? <span aria-hidden="true" style={{ width: 1, alignSelf: "center", height: 16, background: T.border, margin: "0 10px" }} /> : null}
            <Tab active={active === x.id} count={x.count} onClick={x.onClick}>{x.label}</Tab>
          </span>
        );
      })}
    </nav>
  );
}

export interface ScreenProps {
  rail?: string | false;
  topbar?: boolean;
  tabs?: PrimaryNavTab[];
  activeTab?: string;
  children?: ReactNode;
  width?: number;
  height?: number;
  search?: string;
}

/* No default rail item (CC, 2026-10-06). A screen that names none, and the error-boundary fallback and unknown-address
   page the router wraps in this Screen, light the rail item whose route matches the current route (or none). The
   "events" default lit Events on any crashed or unknown page, Calendar included. */
export function Screen({ rail, topbar = true, tabs, activeTab, children, width, height, search }: ScreenProps) {
  const isFrame = width != null;
  const isMobile = useIsMobile();
  useUser(); // re-render chrome on role/tenant change
  const showRail = rail !== false && !isMobile;
  // Page background = T.canvas (#F4F5F7), the design-system page-background
  // token — restored 2026-08-07 after the #E5E5E5 literal experiment.
  return (
    <div style={{ width: isFrame ? width : "100%", height: isFrame ? height : "100%", background: T.canvas, display: "flex", flexDirection: "column", fontFamily: T.font, color: T.n2, fontSize: 16, overflow: "hidden", position: "relative" }}>
      {topbar ? <Topbar search={search} mobileRail={isMobile && rail ? rail : null} /> : null}
      <div style={{ flex: 1, display: "flex", minHeight: 0 }}>
        {showRail ? <IconRail active={rail || undefined} /> : null}
        <main id="main-content" tabIndex={-1} style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", minHeight: 0 }}>
          {tabs ? <PrimaryNav tabs={tabs} active={activeTab} /> : null}
          <ScheduleAckBanner />
          <div style={{ flex: 1, overflow: "auto", display: "flex", flexDirection: "column" }}>{children}</div>
        </main>
      </div>
      {isMobile && rail !== false ? <MobileBottomNav active={rail || undefined} /> : null}
    </div>
  );
}

export function PageHeader({ title, subtitle, actions, breadcrumb }: { title: ReactNode; subtitle?: ReactNode; actions?: ReactNode; breadcrumb?: ReactNode }) {
  const t = useT();
  const nav = useNav();
  // Default "← Dashboard" back-link removed (operator, 2026-09-17). Screens that
  // want a breadcrumb now pass one explicitly; nothing renders by default.
  const back = breadcrumb ?? null;
  return (
    <div style={{ padding: "20px 24px 0", display: "flex", flexDirection: "column", gap: 4, flexShrink: 0 }}>
      {back ? <div style={{ fontSize: 13, color: T.soft, display: "flex", alignItems: "center", gap: 4 }}>{back}</div> : null}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16 }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <h1 style={{ ...tx.h2, fontWeight: 700 }}>{title}</h1>
          {subtitle ? <p style={{ ...tx.help, margin: 0 }}>{subtitle}</p> : null}
        </div>
        {actions ? <div style={{ display: "flex", gap: 8, alignItems: "center", flexShrink: 0 }}>{actions}</div> : null}
      </div>
    </div>
  );
}
